import React, { useState, useRef } from 'react';
import '../styles/home.css';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Spinner from '@cloudscape-design/components/spinner';
import ProgressBar from '@cloudscape-design/components/progress-bar';
import { addUsage } from './add-usage.js';
import { PDFDocument } from 'pdf-lib';
import constants from '../constants.js';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// Render scale used to rasterize each page. ~2.0 gives crisp, high quality
// output (this is a render scale, not a thumbnail scale).
const RENDER_SCALE = 2.0;

const FlattenPdf = ({ isNew }) => {
  const [visible, setVisible] = useState(false);
  const [file, setFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const fileInputRef = useRef(null);

  const resetState = () => {
    setFile(null);
    setErrorMessage('');
    setIsProcessing(false);
    setIsDone(false);
    setProgress(0);
    setProgressLabel('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const toggleModal = () => {
    setVisible(!visible);
    if (!visible) {
      resetState();
    }
  };

  const onDismiss = () => {
    setVisible(false);
    resetState();
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && selectedFile.type === constants.pdfFileType) {
      setFile(selectedFile);
      setErrorMessage('');
    } else {
      setFile(null);
      setErrorMessage(constants.erroredFile);
    }
  };

  const flattenPdf = async () => {
    if (!file) {
      setErrorMessage(constants.pdfFile);
      return;
    }

    setIsProcessing(true);
    setIsDone(false);
    setErrorMessage('');
    setProgress(0);

    try {
      const arrayBuffer = await file.arrayBuffer();

      // Load the PDF with PDF.js for rasterization
      const pdfJsDoc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const totalPages = pdfJsDoc.numPages;

      // Create a new PDF document using pdf-lib
      const outPdf = await PDFDocument.create();

      for (let i = 1; i <= totalPages; i++) {
        setProgressLabel(`Flattening page ${i} of ${totalPages}`);

        const pdfJsPage = await pdfJsDoc.getPage(i);

        // Original page dimensions in points (scale 1) - used to size the output page
        const baseViewport = pdfJsPage.getViewport({ scale: 1.0 });
        const pageWidthPt = baseViewport.width;
        const pageHeightPt = baseViewport.height;

        // High resolution viewport for crisp rasterization
        const viewport = pdfJsPage.getViewport({ scale: RENDER_SCALE });

        let canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        // Fill white background first so transparent areas become white
        // (correct behaviour when flattening transparency/layers).
        context.fillStyle = 'white';
        context.fillRect(0, 0, canvas.width, canvas.height);

        // Render all content (text, graphics, annotations, forms) onto the canvas,
        // baking everything into a single flat layer.
        await pdfJsPage.render({
          canvasContext: context,
          viewport,
          renderInteractiveForms: true,
        }).promise;

        // Embed the rasterized page as a PNG image in the new PDF
        const imgData = canvas.toDataURL('image/png');
        const img = await outPdf.embedPng(imgData);

        // Add a new page sized to the ORIGINAL page point dimensions and draw
        // the flattened image to fill the entire page.
        const newPage = outPdf.addPage([pageWidthPt, pageHeightPt]);
        newPage.drawImage(img, {
          x: 0,
          y: 0,
          width: pageWidthPt,
          height: pageHeightPt,
        });

        // Free the canvas to avoid memory blowup on large PDFs
        canvas.width = 0;
        canvas.height = 0;
        canvas = null;

        setProgress(Math.round((i / totalPages) * 100));
      }

      // Save and auto-download the flattened PDF
      const bytes = await outPdf.save();
      const blob = new Blob([bytes], { type: constants.pdfFileType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `flattened_${file.name}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      addUsage('AWESOME PDF TOOLS FlattenPdf', '1.0', 1, window.location.href);

      setProgress(100);
      setIsProcessing(false);
      setIsDone(true);
    } catch (error) {
      console.error('Flatten failed:', error);
      setIsProcessing(false);
      if (error.message && (error.message.includes('encrypted') || error.message.includes('password'))) {
        setErrorMessage(constants.encryptedFile);
      } else {
        setErrorMessage('Flattening failed. Please try again.');
      }
    }
  };

  const flattenAnother = () => {
    resetState();
  };

  return (
    <div>
      <div className="service-card" onClick={toggleModal}>
        {isNew && <span className="badge badge-new">NEW</span>}
        <div className="service-card-icon">
          <svg viewBox="0 0 24 24" fill="#FF9900" width="40" height="40">
            <path d="M12 2L2 7l10 5 10-5-10-5zm0 7.5L4.21 5.61 12 2.18l7.79 3.43L12 9.5zM2 12l10 5 10-5-1.79-.9L12 15.18 3.79 11.1 2 12zm0 5l10 5 10-5-1.79-.9L12 20.18 3.79 16.1 2 17z" />
          </svg>
        </div>
        <h3 className="service-card-title">Flatten PDF</h3>
        <p className="service-card-desc">
          Quickly and easily merge all layers and elements of your PDF into a single layer
        </p>
        <div className="service-card-action">
          <span>Get Started</span>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
          </svg>
        </div>
      </div>

      <Modal
        onDismiss={onDismiss}
        visible={visible}
        size="small"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={onDismiss}>
                {constants.cancel}
              </Button>
              <Button variant="primary" onClick={flattenPdf} disabled={isProcessing || isDone || file == null}>
                {isProcessing ? <Spinner /> : 'Flatten PDF'}
              </Button>
            </SpaceBetween>
          </Box>
        }
        header="Flatten PDF"
      >
        <div className="bordered-box">
          <div className="container-header">📄 Flatten PDF</div>

          {!isDone ? (
            <div>
              <FormField
                label={constants.uploadFile}
                description={constants.onlyPdfSupported}
                errorText={errorMessage}
              >
                <input
                  ref={fileInputRef}
                  id="flatten-file-input"
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  disabled={isProcessing}
                />
              </FormField>

              {isProcessing && (
                <div style={{ marginTop: '15px' }}>
                  <FormField label="Flattening Progress">
                    <ProgressBar value={progress} label={`${progress}%`} description={progressLabel} />
                  </FormField>
                </div>
              )}
            </div>
          ) : (
            <div style={{ marginTop: '10px' }}>
              <Box variant="p">✅ Your PDF has been flattened and downloaded.</Box>
              <div style={{ marginTop: '10px' }}>
                <Button variant="link" onClick={flattenAnother}>
                  Flatten another
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default FlattenPdf;
